<template>
    <div class="menu-tabs-editor">
        <!-- Controls (Cancel/Save/Create, language selector, visibility toggle)
             live in the parent MenuCollapseItemContent so the editor focuses on
             the phone-preview rendering. -->

        <!-- Phone screen: empty header + empty body + tab bar in footer -->
        <PhoneScreen
            ref="phone"
            :menu="menu"
            :version="version"
            :footer-min-height="FOOTER_MIN_HEIGHT_TABS"
        >
            <template #footer="{ ratio, fontName }">
                <emptyAlert
                    v-if="displayedItems.length === 0"
                    :message="$t('apps.configurator.alert-empty.item-text')"
                    class="my-2"
                />
                <div
                    v-else
                    ref="tabsContainer"
                    class="flex h-full w-full"
                    role="list"
                >
                    <div
                        v-for="item in displayedItems"
                        :key="item.uuid"
                        :data-uuid="item.uuid"
                        :style="tabItemStyle(item, ratio)"
                        :class="cellClass(item)"
                        class="flex flex-col items-center justify-center relative overflow-hidden"
                        role="button"
                        :aria-label="cellAriaLabel(item)"
                    >
                        <!-- Icon (or warning if it can't be resolved) -->
                        <div
                            v-if="hasMissingIcon(item)"
                            :title="$t('apps.configurator.menu-list.icon-missing')"
                            :aria-label="$t('apps.configurator.menu-list.icon-missing')"
                        >
                            <feather-icon icon="AlertTriangleIcon" :style="warningIconStyle(ratio)" />
                        </div>
                        <div v-else :style="tabIconStyle(item, ratio)"></div>

                        <!-- Label (or title-missing warning when no localised title) -->
                        <div
                            v-if="hasMissingTitle(item)"
                            :title="$t('apps.configurator.menu-list.title-missing')"
                            :aria-label="$t('apps.configurator.menu-list.title-missing')"
                        >
                            <feather-icon icon="AlertTriangleIcon" :style="titleWarningIconStyle(ratio)" />
                        </div>
                        <p v-else :style="tabLabelStyle(ratio, fontName)" class="m-0 text-center">{{ itemLabel(item) }}</p>

                        <!-- Invalid-context badge -->
                        <span v-if="isItemInvalidForContext(item)" class="menu-grid-cell__invalid-badge">
                            {{ $t('apps.configurator.menu-list.invalid-context') }}
                        </span>

                        <!-- Drag handle + edit/delete overlay -->
                        <CellHoverActions
                            :read-only="readOnly"
                            :icon-size="Math.round(14 * ratio)"
                            @drag-mousedown="onDragHandleMousedown($event, item)"
                            @edit="$emit('editItem', item)"
                            @delete="onDeleteItem(item)"
                        />
                    </div>
                </div>
            </template>
        </PhoneScreen>
    </div>
</template>

<script>
import emptyAlert from '@/modules/Shared/Components/alert/emptyAlert.vue'
import PhoneScreen from '@/modules/Apps/Components/configurator/_shared/PhoneScreen.vue'
import CellHoverActions from '@/modules/Apps/Components/configurator/_shared/CellHoverActions.vue'
import dragAndDropMixin from '@/modules/Apps/Components/configurator/_shared/dragAndDropMixin'
import '@/modules/Apps/Components/configurator/_shared/menuGridCell.css'
import {
    isWidgetItem,
    isBannerItem,
    getTemplateKind,
    allowedTemplateKindsForContext,
    argbToRgba,
    widthFor,
    heightFor,
    resolveIconUrl,
    CONTEXT_KINDS,
} from '@/modules/Apps/Helpers/menuVisuals'
import {
    FOOTER_MIN_HEIGHT_TABS,
    TAB_ICON_SIZE,
    TAB_LABEL_FONT_SIZE,
    TAB_GAP_BETWEEN_PARTS,
    TAB_VERT_PADDING,
    TAB_HORIZ_PADDING,
} from '@/modules/Apps/Components/configurator/_shared/phoneDimensions'
import {
    getMenuLayout,
    getMenuStyles,
    getMenuResources,
    buildTemplateByUuid,
    recomputeOrdering,
} from '@/modules/Apps/Helpers/menuContext'
import { Version } from '@/modules/Apps/Models/Version'

export default {
    name: 'MenuTabsEditor',

    components: { emptyAlert, PhoneScreen, CellHoverActions },

    mixins: [dragAndDropMixin],

    props: {
        menu:                    { type: Object,  required: true },
        version:                 { type: Version, required: true },
        readOnly:                { type: Boolean, default: false },
        showUnpublished:         { type: Boolean, default: true },
        // Active language for label/icon rendering (parent-owned vs-select).
        selectedLanguageAcronym: { type: String,  default: null },
        editingItemUuid:         { type: String,  default: null },
    },

    data() {
        return {
            // Template-bound constants
            FOOTER_MIN_HEIGHT_TABS,
        }
    },

    computed: {
        // Derived menu context (all version lookups in one place)
        mainLanguage()   { return this.version.mainLanguage() },
        layout()         { return getMenuLayout(this.menu, this.version) },
        styles()         { return getMenuStyles(this.menu, this.version) },
        resources()      { return getMenuResources(this.menu, this.version) },
        languages()      { return this.version.languages },
        templateByUuid() { return buildTemplateByUuid(this.version) },
        previewItems() {
            if (!this.draggingItemUuid || !this.hoverTargetUuid) return null
            if (this.draggingItemUuid === this.hoverTargetUuid) return null
            const items = this.menu.items
            const draggingIdx = items.findIndex(i => i.uuid === this.draggingItemUuid)
            const targetIdx   = items.findIndex(i => i.uuid === this.hoverTargetUuid)
            if (draggingIdx < 0 || targetIdx < 0) return null
            const newItems = items.filter(i => i.uuid !== this.draggingItemUuid)
            const adjustedTarget = newItems.findIndex(i => i.uuid === this.hoverTargetUuid)
            if (adjustedTarget < 0) return null
            const insertAt = draggingIdx < targetIdx ? adjustedTarget + 1 : adjustedTarget
            newItems.splice(insertAt, 0, items[draggingIdx])
            return newItems
        },
        displayedItems() {
            const items = this.previewItems || this.menu.items
            return items.filter(i => this.showUnpublished || i.published)
        },
    },

    watch: {
        // Reset internal state when the parent swaps to a different menu without unmounting
        'menu.uuid'() {
            // Tabs have no hierarchical state; nothing to clear yet, but the hook
            // documents intent so future state additions stay scoped to the active menu.
        },
    },

    methods: {
        // ===== Cell content helpers =====

        isItemSpecial(item) {
            const t = this.templateByUuid[item.item_template_uuid]
            return t ? (isWidgetItem(t) || isBannerItem(t)) : false
        },
        isItemInvalidForContext(item) {
            const t = this.templateByUuid[item.item_template_uuid]
            if (!t) return false
            // Tabs use the per-menu-type template filter; no submenu contexts.
            return !allowedTemplateKindsForContext(CONTEXT_KINDS.ROOT)[getTemplateKind(t)]
        },
        itemLabel(item) {
            // Banners and widgets are language-agnostic config blocks — they
            // identify themselves by `name`, not by localised `titles`.
            if (this.isItemSpecial(item)) return item.name || ''
            const acronym = this.selectedLanguageAcronym
            const titles = item.titles || {}
            return (acronym && titles[acronym]) || item.name || ''
        },

        // ===== Tab-cell styles =====

        tabItemStyle(item, ratio) {
            const isDragging = item.uuid === this.draggingItemUuid
            const style = {
                flex: '1',
                gap: `${TAB_GAP_BETWEEN_PARTS * ratio}px`,
                padding: `${TAB_VERT_PADDING * ratio}px ${TAB_HORIZ_PADDING * ratio}px`,
                cursor: 'default',
                opacity: (isDragging || !item.published) ? '0.3' : '1',
            }
            // Missing-icon cells get a light red tint so the error is visible at a glance.
            if (this.hasMissingIcon(item)) {
                style['background-color'] = 'rgba(239,68,68,0.12)'
            }
            return style
        },
        hasMissingIcon(item) {
            if (!item) return false
            if (this.isItemSpecial(item)) return false
            return !resolveIconUrl(item, this.resources, this.languages, this.selectedLanguageAcronym)
        },
        // True when a tab would show a localised label but the selected-language
        // title is missing — surfaces as a warning glyph instead of falling
        // back to item.name (parity with the home grid behaviour).
        hasMissingTitle(item) {
            if (!item) return false
            if (this.isItemSpecial(item)) return false
            const acronym = this.selectedLanguageAcronym
            if (!acronym) return false
            const titles = item.titles || {}
            return !titles[acronym]
        },
        warningIconStyle(ratio) {
            const size = `${TAB_ICON_SIZE * ratio}px`
            return { width: size, height: size, color: 'rgba(220,38,38,0.95)' }
        },
        titleWarningIconStyle(ratio) {
            const size = `${14 * ratio}px`
            return { width: size, height: size, color: 'rgba(220,38,38,0.95)' }
        },
        tabIconStyle(item, ratio) {
            const size = `${TAB_ICON_SIZE * ratio}px`
            const iconUrl = resolveIconUrl(item, this.resources, this.languages, this.selectedLanguageAcronym)
            const style = {
                width: size,
                height: size,
                'flex-shrink': '0',
                'background-size': 'contain',
                'background-repeat': 'no-repeat',
                'background-position': 'center',
            }
            if (iconUrl) {
                style['background-image'] = `url(${iconUrl})`
            } else {
                style['background-color'] = 'rgba(255,255,255,0.3)'
                style['border-radius'] = `${4 * ratio}px`
            }
            return style
        },
        tabLabelStyle(ratio, fontName) {
            const c = this.styles.main_tabs_normal_color
                ? argbToRgba(this.styles.main_tabs_normal_color)
                : '#ffffff'
            return {
                'font-size': `${(this.styles.main_tabs_font_size || TAB_LABEL_FONT_SIZE) * ratio}px`,
                'font-family': this.styles.main_tabs_font || fontName || 'sans-serif',
                color: c,
                overflow: 'hidden',
                'white-space': 'nowrap',
                'text-overflow': 'ellipsis',
                'max-width': '100%',
                margin: '0',
                'line-height': '1.2',
            }
        },
        cellClass(item) {
            return {
                'menu-grid-cell': true,
                'menu-grid-cell--unpublished': !item.published,
                'menu-grid-cell--special': this.isItemSpecial(item),
                'menu-grid-cell--dragging': item.uuid === this.draggingItemUuid,
                'menu-grid-cell--drop-target': item.uuid === this.hoverTargetUuid,
                'menu-grid-cell--editing':  item.uuid === this.editingItemUuid,
                'menu-grid-cell--missing-icon': this.hasMissingIcon(item),
            }
        },
        cellAriaLabel(item) {
            const label = this.itemLabel(item)
            return !item.published ? `${label} (unpublished)` : label
        },

        // ===== Actions =====

        onDeleteItem(item) {
            this.$vs.dialog({
                type: 'confirm',
                color: 'danger',
                title: this.$t('apps.configurator.menu-list.remove.title'),
                text:  this.$t('apps.configurator.menu-list.remove.text'),
                accept: () => this.$emit('deleteItem', item),
            })
        },
        // ===== DnD mixin hooks =====

        getDragContainer() {
            return this.$refs.tabsContainer
        },
        getCellDimensions(item) {
            const ratio = this.$refs.phone ? this.$refs.phone.ratio : 1
            return {
                width:  widthFor(1, this.layout, ratio),
                height: heightFor(item.rows || 1, this.layout, ratio),
            }
        },
        getCellIconUrl(item) {
            return resolveIconUrl(item, this.resources, this.languages, this.selectedLanguageAcronym)
        },
        // Tabs have no children — no hover-hold-to-enter, no edge-scroll on a fixed footer.
        // Mixin's default no-ops apply.
        commitReorder() {
            if (!this.previewItems) return
            this.pushUndoSnapshot()
            const ref = this.menu.items
            ref.splice(0, ref.length, ...this.previewItems)
            recomputeOrdering(this.menu.items)
        },

        onDragHandleMousedown(event, item) {
            this.startDrag(event, item)
        },

        // ===== Undo hooks (consumed by the DnD mixin) =====

        getMenuSnapshot() {
            return JSON.parse(JSON.stringify(this.menu.items))
        },
        restoreMenuSnapshot(snapshot) {
            this.menu.items.splice(0, this.menu.items.length, ...snapshot)
            recomputeOrdering(this.menu.items)
        },
    },
}
</script>
